import { useEffect, useState } from "react";
import { X, Trash2, Calendar, Flag, User as UserIcon, Folder, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DictaphoneTextarea } from "@/components/DictaphoneTextarea";

const PRIORITIES = [
  { v: "low", l: "Niedrig", color: "bg-zinc-700 text-zinc-200" },
  { v: "normal", l: "Normal", color: "bg-sky-600/30 text-sky-200 border-sky-600/40" },
  { v: "high", l: "Hoch", color: "bg-red-600/30 text-red-200 border-red-600/40" },
];

const STATUSES = [
  { v: "open", l: "Offen" },
  { v: "in_progress", l: "In Arbeit" },
  { v: "done", l: "Erledigt" },
  { v: "cancelled", l: "Abgebrochen" },
];

// Wiedervorlage: minutes before due_date to trigger a push reminder.
// `null` = no reminder.
const REMINDERS = [
  { v: "", l: "Keine Wiedervorlage" },
  { v: "60", l: "1 Stunde vorher" },
  { v: "1440", l: "1 Tag vorher" },
  { v: "2880", l: "2 Tage vorher" },
  { v: "10080", l: "1 Woche vorher" },
];

const toDateInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const TaskModal = ({ open, onClose, task, members = [], onSaved, defaultProjectId = "" }) => {
  const { user } = useAuth();
  const isEdit = !!task?.id;
  const isManager = user?.role === "admin" || user?.role === "sales_manager";

  const [form, setForm] = useState({
    title: "",
    description: "",
    due_date: "",
    priority: "normal",
    project_id: "",
    owner_user_id: "",
    status: "open",
    reminder_minutes_before: "",
  });
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        due_date: toDateInput(task.due_date),
        priority: task.priority || "normal",
        project_id: task.project_id || "",
        owner_user_id: task.owner_user_id || user?.id || "",
        status: task.status || "open",
        reminder_minutes_before: task.reminder_minutes_before != null ? String(task.reminder_minutes_before) : "",
      });
    } else {
      setForm({
        title: "",
        description: "",
        due_date: "",
        priority: "normal",
        project_id: defaultProjectId || "",
        owner_user_id: user?.id || "",
        status: "open",
        reminder_minutes_before: "",
      });
    }
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
  }, [open, task, user, defaultProjectId]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Titel fehlt");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        due_date: form.due_date ? new Date(form.due_date + "T09:00:00").toISOString() : null,
        priority: form.priority,
        project_id: form.project_id || null,
        owner_user_id: form.owner_user_id || undefined,
        reminder_minutes_before: form.reminder_minutes_before ? Number(form.reminder_minutes_before) : null,
      };
      if (isEdit) {
        payload.status = form.status;
        await api.patch(`/tasks/${task.id}`, payload);
        toast.success("Aufgabe aktualisiert");
      } else {
        await api.post("/tasks", payload);
        toast.success("Aufgabe angelegt");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!confirm("Aufgabe wirklich löschen?")) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Aufgabe gelöscht");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="task-modal">
      <div className="bg-[#18181B] border border-zinc-800 rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#18181B] border-b border-zinc-800 px-5 py-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">{isEdit ? "Aufgabe bearbeiten" : "Neue Aufgabe"}</h3>
          <button onClick={onClose} className="h-10 w-10 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center" data-testid="task-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider">Titel *</label>
            <input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              data-testid="task-title-input"
              className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              placeholder="z. B. Angebot nachfassen"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><Calendar size={12} /> Fällig</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => update("due_date", e.target.value)}
                data-testid="task-due-input"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              />
            </div>
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><Flag size={12} /> Priorität</label>
              <select
                value={form.priority}
                onChange={(e) => update("priority", e.target.value)}
                data-testid="task-priority-select"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              >
                {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </div>
          </div>

          {form.due_date && (
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5">
                <Bell size={12} /> Wiedervorlage (Push-Erinnerung)
              </label>
              <select
                value={form.reminder_minutes_before}
                onChange={(e) => update("reminder_minutes_before", e.target.value)}
                data-testid="task-reminder-select"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              >
                {REMINDERS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
              {form.reminder_minutes_before && (
                <p className="mt-1 text-[12px] text-zinc-300 font-medium">
                  Push-Erinnerung wird automatisch versendet (sofern Browser-Benachrichtigungen aktiviert).
                </p>
              )}
            </div>
          )}

          {isEdit && (
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider">Status</label>
              <select
                value={form.status}
                onChange={(e) => update("status", e.target.value)}
                data-testid="task-status-select"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              >
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
          )}

          {isManager && members.length > 0 && (
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><UserIcon size={12} /> Zuweisen an</label>
              <select
                value={form.owner_user_id}
                onChange={(e) => update("owner_user_id", e.target.value)}
                data-testid="task-owner-select"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              >
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}{m.id === user?.id ? " (Ich)" : ""}</option>
                ))}
              </select>
            </div>
          )}

          {projects.length > 0 && (
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><Folder size={12} /> Projekt</label>
              <select
                value={form.project_id || ""}
                onChange={(e) => update("project_id", e.target.value || "")}
                data-testid="task-project-select"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              >
                <option value="">— persönlich (kein Projekt) —</option>
                {projects.slice(0, 100).map((p) => (
                  <option key={p.id} value={p.id}>{p.project_number} · {p.customer_name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider">Notiz</label>
            <DictaphoneTextarea
              value={form.description}
              onChange={(v) => update("description", v)}
              testid="task-description-input"
              rows={3}
              placeholder="Notiz eingeben oder Mikrofon-Button drücken zum Diktieren…"
              className="bg-[#09090B] border-zinc-700 text-zinc-100 focus:border-[#00E575] focus:ring-[#00E575] mt-1"
            />
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            {isEdit ? (
              <button onClick={remove} data-testid="task-delete-btn" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-sm px-3 h-11 rounded-xl border border-red-500/30 hover:border-red-500/60">
                <Trash2 size={16} /> Löschen
              </button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <button onClick={onClose} data-testid="task-cancel-btn" className="px-4 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="task-save-btn" className="px-5 h-11 rounded-xl bg-[#00E575] text-[#09090B] hover:bg-[#00C964] font-semibold text-sm disabled:opacity-50">
                {saving ? "Speichert…" : isEdit ? "Speichern" : "Anlegen"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
